/**
 * useConnectionMetrics - React hook for real-time connection quality monitoring
 *
 * Provides live metrics for:
 * - Connection quality (RTT, packet loss, jitter)
 * - Audio quality (levels, clipping, noise)
 * - Video quality (resolution, frame drops)
 * - Recording health
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  metricsService,
  AllMetrics,
  ConnectionMetrics,
  AudioMetrics,
  VideoMetrics,
  RecordingMetrics,
  QualityLevel,
} from '../lib/metricsService';

// ============================================================================
// Types
// ============================================================================

export interface UseConnectionMetricsOptions {
  /** Polling interval in milliseconds (default: 1000) */
  pollingInterval?: number;
  /** Whether to automatically start polling (default: true) */
  autoStart?: boolean;
  /** Audio stream for level monitoring */
  audioStream?: MediaStream | null;
  /** RTCPeerConnection for WebRTC stats */
  peerConnection?: RTCPeerConnection | null;
}

export interface UseConnectionMetricsResult {
  // Current metrics
  metrics: AllMetrics | null;
  connection: ConnectionMetrics | null;
  audio: AudioMetrics | null;
  video: VideoMetrics | null;
  recording: RecordingMetrics | null;

  // Overall status
  overallHealth: QualityLevel;
  isHealthy: boolean;
  isConnected: boolean;

  // Simplified indicators for UI
  connectionStatus: {
    level: QualityLevel;
    label: string;
    icon: '🟢' | '🟡' | '🔴' | '⚪';
  };
  audioStatus: {
    level: QualityLevel;
    label: string;
    icon: '🟢' | '🟡' | '🔴' | '⚪';
  };

  // Control functions
  startPolling: () => void;
  stopPolling: () => void;
  refreshNow: () => Promise<void>;

  // Loading state
  isPolling: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusIcon(level: QualityLevel): '🟢' | '🟡' | '🔴' | '⚪' {
  switch (level) {
    case 'good': return '🟢';
    case 'fair': return '🟡';
    case 'poor': return '🔴';
    default: return '⚪';
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useConnectionMetrics(
  options: UseConnectionMetricsOptions = {}
): UseConnectionMetricsResult {
  const {
    pollingInterval = 1000,
    autoStart = true,
    audioStream = null,
    peerConnection = null,
  } = options;

  // State
  const [metrics, setMetrics] = useState<AllMetrics | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Refs for tracking initialization
  const isInitializedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Initialize metrics service when streams are available
  useEffect(() => {
    if (audioStream && !isInitializedRef.current) {
      console.log('[useConnectionMetrics] Initializing with audio stream');
      metricsService.initialize(audioStream, peerConnection || undefined);
      isInitializedRef.current = true;
    }
  }, [audioStream, peerConnection]);

  // Update peer connection when it changes
  useEffect(() => {
    if (peerConnection && isInitializedRef.current) {
      metricsService.setPeerConnection(peerConnection);
    }
  }, [peerConnection]);

  // Subscribe to metrics updates
  useEffect(() => {
    if (!isInitializedRef.current) return;

    unsubscribeRef.current = metricsService.subscribe((newMetrics) => {
      setMetrics(newMetrics);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [audioStream]); // Re-subscribe when stream changes

  // Start/stop polling based on autoStart
  useEffect(() => {
    if (autoStart && isInitializedRef.current && !isPolling) {
      metricsService.startPolling(pollingInterval);
      setIsPolling(true);
    }

    return () => {
      // Don't stop on unmount if other components might be using it
      // metricsService.stopPolling();
    };
  }, [autoStart, pollingInterval, audioStream]);

  // Control functions
  const startPolling = useCallback(() => {
    if (isInitializedRef.current) {
      metricsService.startPolling(pollingInterval);
      setIsPolling(true);
    }
  }, [pollingInterval]);

  const stopPolling = useCallback(() => {
    metricsService.stopPolling();
    setIsPolling(false);
  }, []);

  const refreshNow = useCallback(async () => {
    const newMetrics = await metricsService.collectMetrics();
    setMetrics(newMetrics);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      // Note: We don't destroy the service here as other components might use it
    };
  }, []);

  // Derived values
  const connection = metrics?.connection || null;
  const audio = metrics?.audio || null;
  const video = metrics?.video || null;
  const recording = metrics?.recording || null;
  const overallHealth = metrics?.overallHealth || 'unknown';

  const isHealthy = overallHealth === 'good' || overallHealth === 'fair';
  // Consider "connected" if we have either connection OR audio metrics available
  // This allows the indicator to work even without a peer connection (audio-only mode)
  const isConnected = connection?.quality !== 'unknown' || audio?.quality !== 'unknown';

  // Simplified status indicators for UI
  // When no peer connection exists but audio is working, show "Local" status
  // Note: We check connection?.quality !== 'unknown' (not just qualityLabel) because
  // qualityLabel defaults to 'Connecting...' which is truthy, so a simple || fallback won't work
  const hasValidConnection = connection !== null && connection.quality !== 'unknown';
  const hasWorkingAudio = audio !== null && audio.quality !== 'unknown';

  const connectionStatus = {
    level: hasValidConnection ? connection.quality : (hasWorkingAudio ? 'good' as QualityLevel : 'unknown' as QualityLevel),
    label: hasValidConnection ? connection.qualityLabel : (hasWorkingAudio ? 'Local' : 'Connecting...'),
    icon: getStatusIcon(hasValidConnection ? connection.quality : (hasWorkingAudio ? 'good' : 'unknown')),
  };

  const audioStatus = {
    level: audio?.quality || 'unknown',
    label: audio?.qualityLabel || 'No Audio',
    icon: getStatusIcon(audio?.quality || 'unknown'),
  };

  return {
    metrics,
    connection,
    audio,
    video,
    recording,
    overallHealth,
    isHealthy,
    isConnected,
    connectionStatus,
    audioStatus,
    startPolling,
    stopPolling,
    refreshNow,
    isPolling,
  };
}

// ============================================================================
// Simplified Hooks for Specific Use Cases
// ============================================================================

/**
 * Simple hook that just returns connection and audio status
 * Perfect for the compact header indicator
 */
export function useSimpleMetrics(audioStream: MediaStream | null) {
  const {
    connectionStatus,
    audioStatus,
    isHealthy,
    isConnected,
  } = useConnectionMetrics({ audioStream });

  return {
    connectionStatus,
    audioStatus,
    isHealthy,
    isConnected,
  };
}

/**
 * Hook for detailed metrics view (expandable panel)
 */
export function useDetailedMetrics(
  audioStream: MediaStream | null,
  peerConnection: RTCPeerConnection | null
) {
  return useConnectionMetrics({
    audioStream,
    peerConnection,
    pollingInterval: 500, // More frequent updates for detailed view
  });
}

export default useConnectionMetrics;
